// Typography.tsx
import React from "react";

// Define typography variants and their default HTML tags
export type TypographyVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "small" | "caption" | "span";

// Default HTML element mapping for each variant
const defaultElementMap: Record<TypographyVariant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  p: "p",
  small: "small",
  caption: "span",
  span: "span"
};

// Default Tailwind classes for each variant
const variantClasses: Record<TypographyVariant, string> = {
  h1: "text-h3 md:text-h2 lg:text-h1 font-display leading-[125%] tracking-[-0.015em]", // 44px, 125%
  h2: "text-h4 md:text-h3 lg:text-h2 font-display leading-[130%] tracking-[-0.01em]", // 30px, 130%
  h3: "text-h5 md:text-h4 lg:text-h3 font-display leading-[130%] tracking-[-0.01em]", // 28px, 130%
  h4: "text-h6 md:text-h5 lg:text-h4 font-display leading-[140%]", // 24px, 140%
  h5: "text-p md:text-h6 lg:text-h5 font-display leading-[140%]", // 20px, 140%
  h6: "text-small md:text-p lg:text-h6 font-display leading-[140%]", // 18px, 140%
  p: "text-caption md:text-small lg:text-p font-sans leading-[150%]", // 16px, 150%
  small: "text-small font-sans leading-[150%]", // 14px, 150%
  caption: "text-caption font-sans leading-[150%]", // 12px, 150%
  span: "text-span font-sans leading-[150%]" // 10px, 150%
};

// Font weight options
type FontWeight = "thin" | "extralight" | "light" | "normal" | "medium" | "semibold" | "bold" | "extrabold" | "black";

// Color options - can be expanded with your design system"s color palette
type ColorOption = "default" | "none";

// // Color mapping to Tailwind classes
const colorClasses : Record<ColorOption, string> = {
  default: "text-textPrimary",
  none: "",
};

// Component props
interface TypographyProps {
  variant?: TypographyVariant;
  component?: React.ElementType; // Override the default HTML element
  color?: ColorOption;
  weight?: FontWeight;
  className?: string;
  align?: "left" | "center" | "right" | "justify";
  italic?: boolean;
  uppercase?: boolean;
  underline?: boolean;
  gutterBottom?: boolean;
  children: React.ReactNode;
  noWrap?: boolean;
  truncate?: boolean;
}

const Typography: React.FC<TypographyProps> = ({
  variant = "p",
  component,
  color = "default",
  weight="normal",
  className = "",
  align,
  italic = false,
  uppercase = false,
  underline = false,
  gutterBottom = false,
  children,
  noWrap = false,
  truncate = false,
  ...props
}) => {
  // Determine the component to render
  const Component = component || defaultElementMap[variant];

  // Build className based on props
  const classes = [
    variantClasses[variant],
    colorClasses && colorClasses[color],
    weight && `font-${weight}`,
    align && `text-${align}`,
    italic && "italic",
    uppercase && "uppercase",
    underline && "underline",
    gutterBottom && "mb-4",
    noWrap && "whitespace-nowrap",
    truncate && "truncate",
    className
  ].filter(Boolean).join(" ");

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  );
};

export default Typography;